import { formatDate, toIsoDate } from "@utilities/functions";
import { useUser } from "@utilities/hooks";
import { useState, useEffect, useCallback, useMemo } from "react";

export const useControllers = () => {
    
    const { 
        user, isUserInfosIsComplete,
        updateUserError, updateUserPasswordError,
        onUpdatedUserPassword, onUpdatedUserInfos
    } = useUser();

    const [isEditing, setIsEditing] = useState(false);
    const [isLoadingUpdatedUser, setIsLoadingUpdatedUser] = useState(false);

    const [firstname, setFirstname] = useState<string>(user?.firstname || "");
    const [lastname, setLastname] = useState<string>(user?.lastname || "");
    const [phone, setPhone] = useState<string>(user?.phone || "");
    const [email, setEmail] = useState<string>(user?.email || "");
    const [birth, setBirth] = useState<string>(()=> (
        user?.birthDate ? formatDate(user?.birthDate) : ""
    ));

    const [isLoadingPasswordChange, setIsLoadingPasswordChange] = useState<boolean>(false);
    const [isEditingUpdatePassword, setIsEditingUpdatePassword] = useState<boolean>(false);
    const [isValidToUpdatingPassword, setIsValidToUpdatingPassword] = useState<boolean>(false);
    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");

    const isValidUpdatedUserDataForm = useMemo(() => {
        const birthChanged = birth.length > 0 && toIsoDate(user?.birthDate || "") !== birth;
        const firstnameChanged = firstname !== user?.firstname;
        const lastnameChanged = lastname !== user?.lastname;
        const phoneChanged = phone !== user?.phone;
    
        const allFieldsFilled = !!(firstname && lastname && phone && birth);
    
        return allFieldsFilled && (firstnameChanged || lastnameChanged || phoneChanged || birthChanged);
    }, [firstname, lastname, phone, birth, user]);

    useEffect(() => {
        if (!isUserInfosIsComplete) {
            setIsEditing(true)
        }
    }, [isUserInfosIsComplete]);
    
    const onToggleEdit = useCallback(() => setIsEditing(e => !e), [])

    const handleUpdatedUserInfos = useCallback(async()=> {
        if(!isValidUpdatedUserDataForm) return;

        setIsLoadingUpdatedUser(true);
        await onUpdatedUserInfos({
            token: user?.token,
            ...(firstname !== user?.firstname && { firstname }),
            ...(lastname !== user?.lastname && { lastname }),
            ...(phone !== user?.phone && { phone }),
            ...((birth && birth !== toIsoDate(user?.birthDate || "")) && { birthDate: new Date(birth) }),
        });
        setIsLoadingUpdatedUser(false);
        setIsEditing(false);
    }, [
        birth, firstname, isValidUpdatedUserDataForm, 
        lastname, phone, user,
        onUpdatedUserInfos,
    ]);

    const onToggleEditingUpdatedPassword = useCallback(()=> setIsEditingUpdatePassword(true), []);

    const handleUpdatePassword = useCallback(async() => {
        if (!isValidToUpdatingPassword) return;

        setIsLoadingPasswordChange(true);
        await onUpdatedUserPassword(currentPassword, newPassword);
        setIsLoadingPasswordChange(false);
        setIsEditingUpdatePassword(false);
    }, [
        currentPassword, isValidToUpdatingPassword, 
        newPassword, onUpdatedUserPassword,
    ]);

    return {
        // Updated user infos
        user, isUserInfosIsComplete,
        isLoadingUpdatedUser,
        updateUserError,
        isEditing, isValidUpdatedUserDataForm,
        firstname, setFirstname,
        lastname, setLastname,
        phone, setPhone,
        email, setEmail,
        birth, setBirth,
        onToggleEdit,
        handleUpdatedUserInfos,

        // Updated user password
        isLoadingPasswordChange,
        updateUserPasswordError,
        isEditingUpdatePassword, setIsEditingUpdatePassword,
        isValidToUpdatingPassword, setIsValidToUpdatingPassword,
        currentPassword, setCurrentPassword,
        newPassword, setNewPassword,
        confirmPassword, setConfirmPassword,
        onToggleEditingUpdatedPassword, handleUpdatePassword,
    };
};
